import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Database, AlertCircle, Check, Edit2 } from 'lucide-react';

const DATA_TYPES = {
  text: { label: 'Text', color: 'blue', icon: '📝' },
  number: { label: 'Number', color: 'green', icon: '🔢' },
  email: { label: 'Email', color: 'purple', icon: '📧' },
  url: { label: 'URL', color: 'orange', icon: '🔗' },
  phone: { label: 'Phone', color: 'pink', icon: '📞' },
  date: { label: 'Date', color: 'red', icon: '📅' },
  boolean: { label: 'Boolean', color: 'gray', icon: '✓' },
  json: { label: 'JSON', color: 'indigo', icon: '{ }' }
};

const DataTypeDetector = ({ data, target, onTypesDetected, onBack }) => {
  const [detectedTypes, setDetectedTypes] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [sampleData, setSampleData] = useState({});
  const [editingColumn, setEditingColumn] = useState(null);

  const detectDataType = useCallback((values) => {
    const nonEmptyValues = values.filter(v => v !== null && v !== undefined && v !== '');

    if (nonEmptyValues.length === 0) return 'text';

    // Sample first 100 values for analysis
    const sample = nonEmptyValues.slice(0, 100);

    let scores = {
      number: 0,
      email: 0,
      url: 0,
      phone: 0,
      date: 0,
      boolean: 0,
      json: 0,
      text: 0
    };

    sample.forEach(value => {
      const str = value.toString().trim();

      // Number detection
      if (!isNaN(str) && !isNaN(parseFloat(str))) {
        scores.number++;
      }

      // Email detection
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) {
        scores.email++;
      }

      // URL detection
      if (/^https?:\/\//.test(str) || /^www\./.test(str)) {
        scores.url++;
      }

      // Phone detection (various formats)
      if (/^[\+]?[0-9\s\-\(\)]{7,15}$/.test(str.replace(/\s/g, ''))) {
        scores.phone++;
      }

      // Date detection
      if (Date.parse(str) && !isNaN(Date.parse(str))) {
        scores.date++;
      }

      // Boolean detection
      if (/^(true|false|yes|no|y|n|1|0)$/i.test(str)) {
        scores.boolean++;
      }

      // JSON detection
      try {
        JSON.parse(str);
        if (str.startsWith('{') || str.startsWith('[')) {
          scores.json++;
        }
      } catch (e) {
        // Not JSON
      }

      scores.text++;
    });

    // Calculate confidence percentages
    const total = sample.length;
    const confidence = Math.max(...Object.values(scores)) / total;

    // Return the type with highest score if confidence > 0.7, otherwise text
    if (confidence > 0.7) {
      return Object.keys(scores).reduce((a, b) => scores[a] > scores[b] ? a : b);
    }

    return 'text';
  }, []);

  const analyzeData = useCallback(async () => {
    setIsAnalyzing(true);

    // Simulate processing delay for better UX
    await new Promise(resolve => setTimeout(resolve, 1000));

    const types = {};
    const samples = {};

    data.headers.forEach((header, index) => {
      if (header && header.trim()) {
        const columnValues = data.rows.map(row => row[index]);
        const detectedType = detectDataType(columnValues);

        types[header] = detectedType;

        // Get sample values for each column
        const nonEmptyValues = columnValues.filter(v => v !== null && v !== undefined && v !== '');
        samples[header] = nonEmptyValues.slice(0, 5); // First 5 non-empty values
      }
    });

    setDetectedTypes(types);
    setSampleData(samples);
    setIsAnalyzing(false);
  }, [data, detectDataType]);

  useEffect(() => {
    analyzeData();
  }, [analyzeData]);

  const handleTypeChange = (column, newType) => {
    setDetectedTypes(prev => ({
      ...prev,
      [column]: newType
    }));
    setEditingColumn(null);
  };

  const handleContinue = () => {
    onTypesDetected(detectedTypes);
  };

  const renderColumnCard = (header, index) => {
    const type = detectedTypes[header];
    const typeConfig = DATA_TYPES[type];
    const samples = sampleData[header] || [];
    const isEditing = editingColumn === header;

    return (
      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900 truncate">{header}</h4>
          <button
            onClick={() => setEditingColumn(isEditing ? null : header)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <Edit2 size={16} className="text-gray-500" />
          </button>
        </div>

        {isEditing ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(DATA_TYPES).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => handleTypeChange(header, key)}
                  className={`p-2 rounded-md text-sm font-medium border ${
                    type === key
                      ? `border-${config.color}-500 bg-${config.color}-50 text-${config.color}-700`
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {config.icon} {config.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${typeConfig.color}-100 text-${typeConfig.color}-800 mb-3`}>
              <span className="mr-1">{typeConfig.icon}</span>
              {typeConfig.label}
            </div>

            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-600">Sample values:</p>
              {samples.length > 0 ? (
                <ul className="text-xs text-gray-500 space-y-0.5">
                  {samples.map((sample, idx) => (
                    <li key={idx} className="truncate">
                      "{String(sample)}"
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-400 italic">No sample data available</p>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  if (isAnalyzing) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center gap-3 mb-4">
          <Database className="animate-pulse text-blue-600" size={24} />
          <h3 className="text-xl font-semibold">Analyzing Data Types</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Examining {data.headers.length} columns and {data.totalRows} rows...
        </p>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold">Data Type Detection</h3>
          <p className="text-gray-600">
            Review and adjust the detected data types for each column
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Check className="text-green-600" size={16} />
          Analysis complete
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 mt-0.5" size={20} />
          <div className="text-sm">
            <p className="text-blue-800 font-medium mb-1">Data Type Detection Results</p>
            <p className="text-blue-700">
              We've automatically detected data types based on the content of each column.
              You can click the edit icon to manually adjust any type that seems incorrect.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {data.headers.map((header, index) =>
          header && header.trim() ? renderColumnCard(header, index) : null
        )}
      </div>

      <div className="flex items-center justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <ChevronLeft size={16} />
          Back to Upload
        </button>
        <button
          onClick={handleContinue}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Continue to Field Mapping
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default DataTypeDetector;